import { useEffect,useRef,useState,type MouseEvent as TargetedMouseEvent } from "react";
import { useReadingApi } from "./api";
import { cancelBrowserSpeech,speakBrowser } from "./browser-speech";
import { ReadingText } from "./ReadingText";
import { readingDiagnostic } from "./telemetry";
import type { ExplanationState,QuestionsState,Story } from "./types";
import { InlineIcon,NextButton,SPEECH_SUPPORTED } from "./ui";
function cleanWord(value: string) {
  return value.trim().replace(/^[\p{P}\p{S}]+|[\p{P}\p{S}]+$/gu, "");
}

type ReaderProps = Story & {
  onNewStory: () => void;
  questions: QuestionsState;
  onOpenQuestions: () => void;
};

export function Reader({ text, language, storyId, onNewStory, questions, onOpenQuestions }: ReaderProps) {
  const { fetch, actor } = useReadingApi();
  const parts = text.split(/(\s+)/);
  const words = parts.filter((part) => !/^\s+$/.test(part));
  const totalWords = words.length;
  const [activeIndex, setActiveIndex] = useState(0);
  const [explanationOpen, setExplanationOpen] = useState(false);
  const [explanationState, setExplanationState] = useState<ExplanationState>({
    status: "idle",
    text: "",
  });
  const [explanations, setExplanations] = useState<Record<number, string>>({});
  const explanationAbort = useRef<AbortController | null>(null);
  const activeWord = cleanWord(words[activeIndex] || "");
  const finished = activeIndex >= totalWords - 1;
  const wordsRead = Math.min(activeIndex + 1, totalWords);
  const progress = wordsRead / totalWords * 100;
  const previousIcon = language === "en" ? "next" : "previous";
  const nextIcon = language === "en" ? "previous" : "next";

  useEffect(() => {
    explanationAbort.current?.abort();
    cancelBrowserSpeech();
    setActiveIndex(0);
    setExplanationOpen(false);
    setExplanationState({ status: "idle", text: "" });
    setExplanations({});
  }, [text]);

  useEffect(() => {
    document.querySelector<HTMLElement>(finished ? ".reading-root .reader .finish-note" : ".reading-root .word--active")?.scrollIntoView({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
      block: finished ? "nearest" : "start",
    });
  }, [activeIndex, finished, questions.status]);

  useEffect(() => () => {
    explanationAbort.current?.abort();
    cancelBrowserSpeech();
  }, []);

  function selectWord(index: number) {
    explanationAbort.current?.abort();
    cancelBrowserSpeech();
    setExplanationOpen(false);
    setExplanationState({ status: "idle", text: "" });
    setActiveIndex(index);
  }

  function recordWordEvent(eventType: "listen" | "meaning") {
    if (actor.actor === "parent") return;
    // ponytail: best-effort delivery; add a durable queue if offline tracking is needed.
    void fetch("/api/word-events", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({ storyId, eventType, word: activeWord, language, wordIndex: activeIndex }),
    }).then((response) => {
      if (!response.ok) throw new Error("event_save_failed");
    }).catch(() => readingDiagnostic("word_event_save_failed"));
  }

  async function explainActiveWord() {
    const cached = explanations[activeIndex];
    if (cached) {
      setExplanationState({ status: "success", text: cached });
      return;
    }

    const controller = new AbortController();
    explanationAbort.current = controller;
    setExplanationState({ status: "loading", text: "" });

    try {
      const response = await fetch("/api/explain-word", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: controller.signal,
        body: JSON.stringify({ word: activeWord, text, language }),
      });
      const data = await response.json() as { explanation?: string };
      if (controller.signal.aborted) return;
      if (!response.ok || !data.explanation) throw new Error("explanation_failed");
      const explanation = data.explanation.trim();
      setExplanations((values) => ({ ...values, [activeIndex]: explanation }));
      setExplanationState({ status: "success", text: explanation });
    } catch (error) {
      if ((error as Error).name !== "AbortError") {
        setExplanationState({ status: "error", text: "" });
      }
    }
  }

  function toggleExplanation() {
    if (explanationOpen) {
      setExplanationOpen(false);
      return;
    }
    recordWordEvent("meaning");
    setExplanationOpen(true);
    if (
      explanationState.status !== "loading"
      && explanationState.status !== "success"
    ) {
      explainActiveWord();
    }
  }

  function speakActiveWord() {
    recordWordEvent("listen");
    speakBrowser([activeWord], language);
  }

  function advance(event: TargetedMouseEvent<HTMLButtonElement>) {
    const button = event.currentTarget;
    button.classList.remove("is-advancing");
    void button.offsetWidth;
    button.classList.add("is-advancing");
    selectWord(Math.min(activeIndex + 1, totalWords - 1));
  }

  return (
    <div className={`reader ${language === "en" ? "reader--english" : ""}`}>
      <article
        className="paper"
        lang={language}
        dir={language === "en" ? "ltr" : "rtl"}
      >
        <ReadingText parts={parts} activeIndex={activeIndex} onWordClick={selectWord} />
      </article>

      {finished && (
        <div className="finish-note" role="status">
          <p className="finish-note__text">
            <span aria-hidden="true">📖</span> הגעתם לסוף הטקסט. כל הכבוד!
          </p>
          {questions.status === "pending" && (
            <p className="finish-note__pending">
              <span>מכינים שאלות על הטקסט</span>
              <span className="word-explanation__dots" aria-hidden="true">•••</span>
            </p>
          )}
          {questions.status === "failed" && (
            <p className="finish-note__pending">
              לא הצלחנו להכין שאלות לטקסט הזה.
              {questions.retry && <button type="button" onClick={questions.retry}>נסו שוב</button>}
            </p>
          )}
        </div>
      )}

      <footer className="reader-footer">
        <div className="reader-footer__panel">
          <div className="reader-footer__inner">
            {explanationOpen && (
              <section
                className="word-explanation"
                id="word-explanation"
                tabIndex={0}
                aria-label="פירוש המילה"
                aria-live="polite"
              >
                {explanationState.status === "loading" ? (
                  <div className="word-explanation__loading">
                    <span>רגע, בודקים</span>
                    <span className="word-explanation__dots" aria-hidden="true">
                      •••
                    </span>
                  </div>
                ) : explanationState.status === "error" ? (
                  <div className="word-explanation__error">
                    <span>לא הצלחנו להסביר.</span>
                    <button type="button" onClick={explainActiveWord}>
                      נסו שוב
                    </button>
                  </div>
                ) : explanationState.status === "success" ? (
                  <p>
                    <bdi className="word-explanation__word" dir="auto">
                      {activeWord}
                    </bdi>
                    <span aria-hidden="true"> — </span>
                    {explanationState.text}
                  </p>
                ) : null}
              </section>
            )}

            <div className="reader-footer__meta">
              <button
                className={`dock-button ${explanationOpen ? "is-open" : ""}`}
                type="button"
                aria-expanded={explanationOpen}
                aria-controls="word-explanation"
                aria-label={`פירוש למילה ${activeWord}`}
                disabled={!activeWord}
                onClick={toggleExplanation}
              >
                <InlineIcon name="bookOpenText" />
                <span>{explanationOpen ? "סגירה" : "פירוש"}</span>
              </button>

              <button
                className="dock-button"
                type="button"
                aria-label={`השמעת המילה ${activeWord}`}
                disabled={!activeWord || !SPEECH_SUPPORTED}
                onClick={speakActiveWord}
              >
                <InlineIcon name="volume" />
                <span>השמע</span>
              </button>

              <button
                className="dock-button"
                type="button"
                aria-label="אחורה למילה הקודמת"
                disabled={activeIndex === 0}
                onClick={() => selectWord(Math.max(activeIndex - 1, 0))}
              >
                <InlineIcon name={previousIcon} />
                <span>אחורה</span>
              </button>

            </div>

            <div className={`reader-footer__actions ${finished ? "reader-footer__actions--finished" : ""}`}>
              {finished ? (
                <>
                  <button
                    className="finish-note__button"
                    type="button"
                    disabled={questions.status !== "ready"}
                    onClick={onOpenQuestions}
                  >
                    <InlineIcon name="questions" />
                    <span>לשאלות על הטקסט</span>
                  </button>
                  <button
                    className="dock-button"
                    type="button"
                    aria-label="טקסט חדש"
                    title="טקסט חדש"
                    onClick={onNewStory}
                  >
                    <InlineIcon name="refresh" />
                  </button>
                </>
              ) : (
                <NextButton
                  done={false}
                  label="למילה הבאה"
                  icon={nextIcon}
                  progress={progress}
                  onClick={advance}
                />
              )}
            </div>

            <div
              className="sr-only"
              role="progressbar"
              aria-label="התקדמות בקריאה"
              aria-valuemin={1}
              aria-valuemax={totalWords}
              aria-valuenow={wordsRead}
              aria-valuetext={`${wordsRead} מתוך ${totalWords} מילים`}
            />
          </div>
        </div>
      </footer>
    </div>
  );
}

